/**
 * AI Retry Logic — keeps trying until a reply actually comes back.
 *
 * Works alongside aiCooldown.js:
 *   - aiCooldown = BEFORE the call (prevents rapid-fire requests)
 *   - aiRetry = AFTER a failed response (retries with backoff)
 *
 * ── WHY TIMEOUTS ARE RETRIED (2026-07-29) ───────────────────────────────────
 * Haley: "can you make sure that timeouts auto regenerate until an ai response
 * is sent."
 *
 * This module used to retry ONE condition — HTTP 529 — and had an explicit
 * `catch` that rethrew everything else with the comment "don't retry network
 * failures". A timeout is an AbortError, which is a network failure, so it went
 * straight through: the caller's 90s AbortController fired, the error escaped,
 * and the surface rendered a dead-end placeholder ("*Kya pauses.* [AI error:
 * Request timed out]") that you then had to notice and manually regenerate.
 *
 * Now a timeout is treated as what it is — a turn that didn't happen — and the
 * request is reissued automatically.
 *
 * Two things this must get right:
 *
 *   1. A RETRY NEEDS A FRESH AbortController. If the caller owns the signal and
 *      it has already aborted, every subsequent attempt aborts instantly and
 *      the retry loop is a no-op that just burns the clock. So when `timeoutMs`
 *      is supplied, this function owns the controller and makes a new one per
 *      attempt.
 *   2. A USER-CANCELLED request must NOT be retried. If the caller passes its
 *      own `signal` (navigating away, switching conversations), that abort is
 *      intentional and ends the loop immediately.
 *
 * Retries are BOUNDED, deliberately. Each attempt is a full-price generation
 * against a ~40k-token prompt, and an unbounded loop against a provider having
 * a bad day is a runaway bill with no one watching. After the attempts are
 * spent the error is thrown as before, so the surface can show its retry
 * affordance — the difference is you now only see that after several genuine
 * attempts rather than on the first blip.
 *
 * ── REASONING-ON (shape A2, 2026-09-08) ─────────────────────────────────────
 * Two server answers are a FULL generation that was already billed:
 *   504 { code: 'deadline' }      — the 120 s server deadline fired
 *   502 { code: 'budget_spent' }  — the whole budget went to reasoning
 * Reissuing either doubles the bill for the same outcome, so they are returned
 * to the caller untouched no matter how many attempts remain. The body is
 * peeked on a clone; the caller still reads it normally. Interactive callers
 * (RP, DM, group, phone, drafts) now pass `maxAttempts: 1` outright — with a
 * 40-60 s median reply, a silent second attempt is a two-minute typing dot.
 */

import { isBilledFailureCode } from './aiTimeouts.js';

const MAX_ATTEMPTS = 2;               // legacy 529-only behaviour
const TIMEOUT_MAX_ATTEMPTS = 4;       // when this function owns the timeout
const BASE_DELAY_MS = 2000;

// Status codes worth another go: 529 (Anthropic overloaded), plus the gateway
// family an edge function throws when it is slow or restarting.
const RETRYABLE_STATUS = new Set([429, 500, 502, 503, 504, 529]);

// The two gateway statuses ai-proxy also uses for its billed terminal failures.
const BILLED_FAILURE_STATUS = new Set([502, 504]);

async function isBilledFailure(response) {
  try {
    const body = await response.clone().json();
    return isBilledFailureCode(body?.code);
  } catch {
    return false;
  }
}

/**
 * Fetch with backoff on overload, gateway errors and timeouts.
 *
 * @param {string} url
 * @param {RequestInit} options - `signal` here is treated as USER cancellation.
 * @param {Object} [opts]
 * @param {number} [opts.timeoutMs]   Per-attempt timeout. When set, this
 *                                    function owns the AbortController so each
 *                                    retry gets a fresh one.
 * @param {number} [opts.maxAttempts] 1 = exactly one attempt, never a retry.
 * @param {function(number, number): void} [opts.onRetry] - (nextAttempt, maxAttempts)
 * @param {function(): void} [opts.onDone]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, options = {}, opts = {}) {
  const {
    timeoutMs = 0,
    maxAttempts = timeoutMs ? TIMEOUT_MAX_ATTEMPTS : MAX_ATTEMPTS,
    baseDelayMs = BASE_DELAY_MS, // injectable so tests don't sleep for real
    onRetry,
    onDone,
  } = opts;

  const userSignal = options.signal || null;
  let lastResponse = null;
  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    // The user gave up between attempts — stop, don't spend another call.
    if (userSignal?.aborted) break;

    let controller = null;
    let timer = null;
    let timedOut = false;
    const attemptOptions = { ...options };

    if (timeoutMs > 0) {
      controller = new AbortController();
      timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
      attemptOptions.signal = controller.signal;
      // Propagate a user cancellation into the per-attempt controller so an
      // in-flight request is still interrupted when they navigate away.
      if (userSignal) {
        if (userSignal.aborted) controller.abort();
        else userSignal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }

    try {
      const response = await fetch(url, attemptOptions);
      lastResponse = response;
      lastError = null;

      if (!RETRYABLE_STATUS.has(response.status)) {
        if (onDone) onDone();
        return response;
      }

      // Billed in full server-side — NEVER reissued (see header).
      if (BILLED_FAILURE_STATUS.has(response.status) && await isBilledFailure(response)) {
        if (onDone) onDone();
        return response;
      }

      if (attempt < maxAttempts) {
        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        console.log(`[AIRetry] HTTP ${response.status}, retrying in ${delay}ms (attempt ${attempt}/${maxAttempts})`);
        if (onRetry) onRetry(attempt + 1, maxAttempts);
        await sleep(delay);
      }
    } catch (err) {
      lastError = err;
      const isAbort = err?.name === 'AbortError';

      // Intentional cancellation — surface it, never retry it.
      //
      // The discriminator is the USER's signal, not our own `timedOut` flag.
      // The flag races: the rejection and the timer callback are separate
      // tasks, so an abort can surface before `timedOut` has been set and a
      // genuine timeout would be misread as a cancellation and never retried.
      // Whether the user cancelled is not a race — their signal either aborted
      // or it didn't.
      if (isAbort && userSignal?.aborted) {
        if (onDone) onDone();
        throw err;
      }

      const retryable = isAbort /* our timeout */ || err?.name === 'TypeError' /* network */;
      if (!retryable || attempt >= maxAttempts) {
        if (onDone) onDone();
        throw err;
      }

      const delay = baseDelayMs * Math.pow(2, attempt - 1);
      console.warn(`[AIRetry] ${timedOut ? `timed out after ${timeoutMs}ms` : 'network error'}, retrying in ${delay}ms (attempt ${attempt}/${maxAttempts})`);
      if (onRetry) onRetry(attempt + 1, maxAttempts);
      await sleep(delay);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  if (onDone) onDone();
  if (lastResponse) {
    console.warn(`[AIRetry] All ${maxAttempts} attempts failed (last status ${lastResponse.status})`);
    return lastResponse;
  }
  throw lastError || new Error('AI request failed after all retries');
}

/**
 * Sleep helper
 * @param {number} ms
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
